/**
 * field.tsx - Primitivos de formulario da biblioteca base.
 *
 * Field (label + hint + error) envolvendo Input/Select/Textarea.
 * Substitui os inputStyle/labelStyle/selectorStyle copiados por pagina.
 * Foco SEMPRE visivel: anel via --color-accent (classe ui-control em
 * ui-base.css); corrige o outline:none sem substituto da semente.
 */

import type {
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";
import "./ui-base.css";

// Base compartilhada dos controles (radius de input = --radius, 4px)
const controlStyle: CSSProperties = {
  fontFamily: '"Inter", sans-serif',
  fontSize: "0.8125rem",
  padding: "0.5rem 0.75rem",
  borderRadius: "var(--radius)",
  border: "1px solid var(--color-bg-border)",
  background: "var(--color-bg-surface)",
  color: "var(--color-text-primary)",
  width: "100%",
  boxSizing: "border-box",
};

export function Field({
  label,
  htmlFor,
  hint,
  error,
  style,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", minWidth: 0, ...style }}>
      <label
        htmlFor={htmlFor}
        style={{
          fontFamily: '"Inter", sans-serif',
          fontSize: "0.75rem",
          fontWeight: 600,
          color: "var(--color-text-muted)",
        }}
      >
        {label}
      </label>
      {children}
      {error != null ? (
        <span
          role="alert"
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.75rem",
            color: "var(--color-status-red)",
          }}
        >
          {error}
        </span>
      ) : hint != null ? (
        <span
          style={{
            fontFamily: '"Inter", sans-serif',
            fontSize: "0.75rem",
            color: "var(--color-text-muted)",
          }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function Input({ className, style, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn("ui-control", className)}
      style={{ ...controlStyle, ...style }}
      {...props}
    />
  );
}

export function Select({ className, style, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn("ui-control", className)}
      style={{ ...controlStyle, cursor: "pointer", ...style }}
      {...props}
    >
      {children}
    </select>
  );
}

export function Textarea({ className, style, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn("ui-control", className)}
      style={{ ...controlStyle, resize: "vertical", minHeight: "72px", ...style }}
      {...props}
    />
  );
}
